// ─────────────────────────────────────────────────────────────
//  Protocool – Konfiguration der Protokoll-Links & Formular-Schemata
//
//  "Protocool" ist die interne Protokollierungs-Maschine. Jeder Kunde aus der
//  Sales Pipeline (salesgod-research/customers) erscheint hier als Objekt. Pro
//  Kunde gibt es mehrere teilbare, passwortgeschuetzte Links. Ueber diese Links
//  fuellt z. B. der Team-Copywriter oder der Outreach-Manager im Call ein
//  Formular aus – die Antworten landen am Kundenobjekt
//  (customers/<slug>/protocool/<linkKey>) und sind im Dashboard sofort sichtbar.
//
//  Dieses Modul wird von BEIDEN Seiten genutzt:
//   - der oeffentlichen Share-Seite (ProtocoolForm.js)
//   - dem Dashboard-Tab (Admin/Protocool.js)
//  damit Formular-Felder & Labels nur an EINER Stelle definiert sind.
// ─────────────────────────────────────────────────────────────

// Reihenfolge & Metadaten aller 4 Protokoll-Links. `active: false` rendert den
// Link als "Coming soon"-Platzhalter im Drawer (noch nicht teilbar/ausfuellbar).
//
// Felder:
//  - stage     : kurzer Ablauf-Marker ("Step 1") – macht die Reihenfolge sichtbar
//  - label     : klarer Name des Protokolls
//  - subtitle  : was dieses Protokoll abklaert
//  - audience  : WER es im Call ausfuellt
//  - runBy     : WER das Protokoll fuehrt (Team-Lead)
//  - instructions : Schritt-fuer-Schritt-Ablauf fuer die Team-Leiterin
export const PROTOCOOL_LINKS = [
  {
    key: 'brainstorming',
    order: 1,
    stage: 'Team Copywriter',
    label: 'Copywriting Detailing Call',
    subtitle: 'Audit, Feedback & Feinschliff der Texte',
    audience: 'Team Copywriter',
    runBy: 'Team Lead',
    accent: '#7c9df0',
    active: true,
    instructions: [
      'Generate the share link below and copy the link + password.',
      'Send it to the Team Copywriter before the call.',
      'On the call, work through the agenda step by step.',
      'Hit Save \u2014 the answers land on this customer automatically and show up right here.',
    ],
  },
  {
    key: 'outreach_prep',
    order: 2,
    stage: 'Outreach Manager',
    label: 'Copywriting Detailing Call',
    subtitle: 'Outreach Manager \u00b7 Setup & beste Texte',
    audience: 'Outreach Manager',
    runBy: 'Team Lead',
    accent: '#d8a85a',
    active: true,
    instructions: [
      'Generate the share link below and copy the link + password.',
      'Send it to the Outreach Manager before the call.',
      'On the call: tick off the pre-checklist so the structure is confirmed.',
      'Drop the 3 dynamically generated texts you both liked \u2014 they flow into the Research Center email vault under the "Detailing Call" tag.',
    ],
  },
  {
    key: 'link3',
    order: 3,
    stage: 'Step 3',
    label: 'Protocol 3',
    subtitle: 'Coming soon',
    audience: '',
    runBy: '',
    accent: '#4cb89a',
    active: false,
    instructions: [],
  },
  {
    key: 'link4',
    order: 4,
    stage: 'Step 4',
    label: 'Protocol 4',
    subtitle: 'Coming soon',
    audience: '',
    runBy: '',
    accent: '#c47b76',
    active: false,
    instructions: [],
  },
];

// ── Tier-Skalen (mehrstufig, mit Beschreibung pro Stufe) ───────
// `type: 'tier'` rendert die Stufen als Reihe; die Beschreibung der gewaehlten
// Stufe poppt darunter auf. Macht die Auswahl spannender als ja/teils/nein.
const CLEAN_TIERS = [
  { value: 1, label: 'Schludrig', tone: 'bad', desc: 'Mehrere grobe Patzer: falsche Platzhalter, Tippfehler, kaputte Links.' },
  { value: 2, label: 'Unsauber', tone: 'bad', desc: 'Ein paar deutliche Fehler, die sofort auffallen.' },
  { value: 3, label: 'Fast sauber', tone: 'mid', desc: 'Nur noch Kleinigkeiten, kaum spürbar.' },
  { value: 4, label: 'Sauber', tone: 'good', desc: 'Keine echten Fehler mehr drin – durchdacht verschickt.' },
  { value: 5, label: 'Pixelperfekt', tone: 'good', desc: 'Komplett durchgecheckt, makellos rausgegangen.' },
];

// ── Checklisten ─────────────────────────────────────────────────
// `type: 'checklist'` ist Mehrfachauswahl (Werte als Array).
//  - Optionen mit `points` -> Gamification (Fortschrittsbalken).
//  - Optionen mit `desc`   -> Detail-Text poppt auf, sobald angehakt.

// Schritt 2 (Team-Copywriter): welches Feedback wurde gegeben? Interaktiv –
// die Details erscheinen erst beim Anhaken. Kein Punktestand (reines Feedback).
const FEEDBACK_ITEMS = [
  { value: 'details', label: 'Mehr Augenmerk auf Details', desc: 'Tippfehler, Formatierung, Konsistenz – die Kleinigkeiten entscheiden über Profi vs. Spam.' },
  { value: 'templates', label: 'Bessere Template-Einstellung & Prompt-Anweisungen', desc: 'Dynamische Teile sauberer steuern, klarere Prompts für konstante Qualität.' },
  { value: 'audience', label: 'Mehr Fokus auf die richtige Zielgruppe', desc: 'Die richtigen Leute ansprechen – Relevanz schlägt Reichweite.' },
  { value: 'hook', label: 'Stärkerer Hook / Opener', desc: 'Die erste Zeile muss sofort neugierig machen, sonst wird nicht weitergelesen.' },
  { value: 'offer', label: 'Verführerischeres Angebot, mehr Value vorab', desc: 'Konkreter Mehrwert + Risiko-Umkehr (Garantien, "kostenlos für X").' },
  { value: 'personalization', label: 'Maßgeschneiderte Personalisierung', desc: 'Icebreaker, der wirkt, als wäre er nur für diesen einen Empfänger geschrieben.' },
  { value: 'human', label: 'Menschlicher, authentischer Ton', desc: 'Weniger KI-haft, mehr wie von einer echten Person geschrieben.' },
  { value: 'cta', label: 'Klarerer Call-to-Action', desc: 'Ein einziger, einfacher nächster Schritt – kein "vielleicht mal Zoom?".' },
];

// Schritt 3 (Team-Copywriter): Qualitäts-Check nach der Überarbeitung. Mit
// Punkten – bestätigt sichtbar, dass die Verbesserungen Wirkung zeigen.
const QUALITY_CHECK_ITEMS = [
  { value: 'offer', label: 'Angebot ist verführerisch', points: 10 },
  { value: 'opener', label: 'Opener / Icebreaker ist maßgeschneidert', points: 10 },
  { value: 'ai_good', label: 'KI-Template setzt die Texte gut um', points: 10 },
  { value: 'human', label: 'Texte sind menschlich & authentisch', points: 10 },
  { value: 'send_ready', label: 'Alles versandbereit', points: 15 },
];

// Checkliste fuer den Outreach Manager: was im Call mit dem Team-Copywriter
// passiert ist und danach umgesetzt wurde.
const SETUP_CHECK_ITEMS = [
  {
    value: 'sent_reviewed',
    label: 'Team-Copywriter hat die bereits versendeten Texte geprüft',
    desc: 'Gemeinsam die Mails durchgegangen, die schon raus sind - Status quo verstanden.',
    points: 10,
  },
  {
    value: 'feedback_given',
    label: 'Team-Copywriter hat Feedback zu den Texten gegeben',
    desc: 'Konkrete Empfehlungen und Verbesserungspunkte liegen auf dem Tisch.',
    points: 15,
  },
  {
    value: 'templates_improved',
    label: 'Neue Verbesserungen in Templates umgesetzt',
    desc: 'Das Feedback ist in den dynamischen Templates in SalesGod eingearbeitet.',
    points: 15,
  },
];

// ─────────────────────────────────────────────────────────────
//  Formular-Schemata pro Link.
//
//  Feld-Typen:
//   - 'rating'    : Skala 1–10 (Stärke-Meter, min/max) – zeigt zusätzlich
//                   ein Qualitäts-Urteil (z. B. 10/10 = "Excellent").
//   - 'tier'      : mehrstufige Skala (z. B. 1–5) mit Beschreibung pro Stufe,
//                   die beim Auswählen darunter aufpoppt
//   - 'segment'   : farbcodiertes 3-Stufen-Segmented-Control (options mit tone)
//   - 'checklist' : Mehrfachauswahl; Optionen optional mit `points`
//                   (Gamification) und/oder `desc` (Detail poppt beim Anhaken auf)
//   - 'emails'    : Repeater für E-Mail-Texte (subject/body), max. n
//   - 'protocol'  : KI-Transkript als Datei-Upload ODER Freitext
//   - 'note'      : kurzer, mehrzeiliger Freitext
//
//  Felder können `showWhen: { field, filled }` tragen – dann erscheinen sie
//  erst, wenn das referenzierte Feld befüllt ist (dynamisch & interaktiv).
//  Abschnitte tragen `stage` (Agenda-Schritt) + optional `help` + `accent`.
// ─────────────────────────────────────────────────────────────
export const PROTOCOOL_FORMS = {
  brainstorming: {
    key: 'brainstorming',
    title: 'Copywriting Detailing Call',
    intro:
      'Geführter Ablauf für den Call mit dem Team-Copywriter. Geht die Schritte der Reihe nach durch – speichern könnt ihr jederzeit, ihr könnt auch später weiter editieren.',
    sections: [
      {
        id: 'audit',
        stage: 'Schritt 1',
        title: 'Audit der bereits versendeten Texte',
        accent: '#7c9df0',
        fields: [
          {
            id: 'outreach_started',
            type: 'toggle',
            label: 'Wurden bereits Texte versendet & hat der Outreach begonnen?',
            help: 'Wenn ja, geht es zuerst durch das Audit der bereits versendeten Texte. Wenn nein, springst du direkt zu Feedback & Qualität.',
            onLabel: 'Ja, bereits versendet',
            offLabel: 'Nein, noch nicht',
          },
          {
            id: 'sent_quality',
            type: 'rating',
            label: 'Qualität der Texte die bereits versendet wurden?',
            min: 1,
            max: 10,
            required: true,
            showWhen: { field: 'outreach_started', notEquals: 'no' },
          },
          {
            id: 'sent_quality_note',
            type: 'note',
            label: 'Kurz erklären – was fällt auf?',
            placeholder: 'In diesem Zustand habe ich die Texte vorgefunden …',
            showWhen: { allOf: [{ field: 'outreach_started', notEquals: 'no' }, { field: 'sent_quality', filled: true }] },
          },
          {
            id: 'audit_clean',
            type: 'tier',
            label: 'Sauber rausgeschickt – keine groben Fehler?',
            help: 'Wichtig: Viele lassen unkonzentriert grobe Fehler durch – falsche Platzhalter, Tippfehler, kaputte Links, doppelte Sätze. Wie sauber war es wirklich?',
            options: CLEAN_TIERS,
            showWhen: { field: 'outreach_started', notEquals: 'no' },
          },
          {
            id: 'audit_clean_note',
            type: 'note',
            label: 'Was genau ist aufgefallen?',
            placeholder: 'Welche Fehler oder Auffälligkeiten gab es? Kurz festhalten.',
            showWhen: { allOf: [{ field: 'outreach_started', notEquals: 'no' }, { field: 'audit_clean', filled: true }] },
          },
        ],
      },
      {
        id: 'feedback',
        stage: 'Schritt 2',
        title: 'Verbesserungen & Feedback',
        accent: '#d8a85a',
        fields: [
          {
            id: 'feedback_given',
            type: 'checklist',
            label: 'Welches Feedback wurde gegeben & umgesetzt?',
            options: FEEDBACK_ITEMS,
            required: true,
          },
        ],
      },
      {
        id: 'quality',
        stage: 'Schritt 3',
        title: 'Qualität jetzt verbessert?',
        help: 'Bestätigt, dass die Überarbeitung Wirkung zeigt – jeder Punkt zählt.',
        accent: '#4cb89a',
        fields: [
          {
            id: 'quality_check',
            type: 'checklist',
            label: 'Die Texte sind jetzt besser',
            options: QUALITY_CHECK_ITEMS,
          },
        ],
      },
      {
        id: 'wrap',
        stage: 'Schritt 4',
        title: 'Erkenntnisse & nächste Schritte',
        accent: '#7c9df0',
        fields: [
          {
            id: 'insights',
            type: 'note',
            label: 'Wichtigste Erkenntnisse aus dem Call',
            placeholder: 'Was ist aufgefallen? Welche Idee steckt dahinter?',
          },
          {
            id: 'goals',
            type: 'note',
            label: 'Ziele bis zum nächsten Meet-up (in ~2 Wochen)',
            placeholder: 'Was soll bis dahin erreicht sein?',
          },
          {
            id: 'open_questions',
            type: 'note',
            label: 'Offene Fragen an den Kunden',
            placeholder: 'Was kann nur der Kunde selbst klären?',
          },
        ],
      },
    ],
  },
  outreach_prep: {
    key: 'outreach_prep',
    title: 'Copywriting Detailing Call',
    intro:
      'Nach dem Call mit dem Team-Copywriter: festhalten, was passiert ist, wie wertvoll die Session war – und die 3 besten Texte nach den Verbesserungen ablegen. Sie fließen automatisch ins Research Center (E-Mail-Vault, Tag "Detailing Call").',
    sections: [
      {
        id: 'texts',
        stage: 'Ergebnis',
        title: 'Die 3 besten Texte nach den Verbesserungen',
        help: 'Die drei stärksten Texte nach dem Feinschliff mit dem Team-Copywriter – nicht der alte Stand, sondern die verbesserte Version.',
        accent: '#4cb89a',
        fields: [
          {
            id: 'best_texts',
            type: 'emails',
            label: 'Jetzt hochladen',
            help: 'Legt hier die 3 besten Texte nach den Verbesserungen ab. Landen automatisch im Research Center unter dem Tag „Detailing Call".',
            subjectPlaceholder: 'Betreff nach Verbesserung',
            bodyPlaceholder: 'Den verbesserten Text hier einfügen …',
            max: 3,
            required: true,
          },
        ],
      },
      {
        id: 'setup',
        stage: 'Nach dem Call',
        title: 'Session & Umsetzung',
        accent: '#d8a85a',
        fields: [
          {
            id: 'setup_checklist',
            type: 'checklist',
            label: 'Was wurde erledigt?',
            help: 'Hak ab, was im Call passiert und danach umgesetzt wurde.',
            options: SETUP_CHECK_ITEMS,
            descAlways: true,
            required: true,
          },
          {
            id: 'session_bullets',
            type: 'note',
            label: 'Kurze Bullet Points zur Session',
            placeholder: '• Was war das Wichtigste?\n• Was hat sich verbessert?\n• Was fällt noch auf?',
          },
          {
            id: 'session_value',
            type: 'rating',
            label: 'Wie wirksam war die Session? Wie viel Value für die Kampagne?',
            min: 1,
            max: 10,
            verdictVariant: 'session',
            required: true,
          },
          {
            id: 'session_value_note',
            type: 'note',
            label: 'Kurz kommentieren – warum diese Bewertung?',
            placeholder: 'Was hat den meisten Value gebracht? Was hätte noch besser sein können?',
            showWhen: { field: 'session_value', filled: true },
          },
          {
            id: 'open_questions',
            type: 'note',
            label: 'Fragen & Unklarheiten',
            placeholder: 'Wo braucht ihr noch Hilfe? Was ist unklar geblieben oder offen?',
          },
        ],
      },
      {
        id: 'protocol',
        stage: 'Transkript',
        title: 'KI-Transkript vom Call',
        help: 'Die KI-Transkription des Calls – als Datei hochladen oder den Text hier einfügen.',
        accent: '#7c9df0',
        fields: [
          {
            id: 'call_protocol',
            type: 'protocol',
            label: 'KI-Transkript',
            help: 'Die automatische Transkription aus dem Call. Datei (z. B. Export) oder Text einfügen – landet bei uns im Backend.',
            textPlaceholder: 'KI-Transkript hier einfügen …',
            fileHint: 'Transkript-Datei wählen (max. 5 MB)',
          },
        ],
      },
    ],
  },
};

// ── Helpers (rein, ohne Seiteneffekte) ──────────────────────────

export const getProtocoolLink = (key) =>
  PROTOCOOL_LINKS.find((l) => l.key === key) || null;

export const getProtocoolForm = (key) => PROTOCOOL_FORMS[key] || null;

// Flache Liste aller Felder eines Formulars.
export const allFormFields = (form) =>
  form && Array.isArray(form.sections)
    ? form.sections.flatMap((s) => s.fields || [])
    : [];

// Ist ein Feldwert "befuellt"? (fuer Fortschritts-/Status-Anzeige)
export function isFieldFilled(value) {
  if (value == null) return false;
  if (Array.isArray(value)) {
    if (value.length === 0) return false;
    // Array aus Objekten (z. B. E-Mail-Texte): mind. eines mit echtem Inhalt.
    return value.some((item) =>
      item && typeof item === 'object'
        ? Object.values(item).some((v) => isFieldFilled(v))
        : isFieldFilled(item)
    );
  }
  if (typeof value === 'string') return value.trim().length > 0;
  if (typeof value === 'number') return !Number.isNaN(value);
  if (value && typeof value === 'object') {
    if (value.mode === 'text') return String(value.text || '').trim().length > 0;
    if (value.mode === 'file') return !!(value.file && String(value.file.url || '').trim());
  }
  return Boolean(value);
}

// Soll ein Feld aktuell angezeigt werden? `showWhen: { field, filled }` blendet
// es ein, sobald das referenzierte Feld befuellt ist (dynamische Formulare).
export function isFieldVisible(field, responses) {
  const cond = field && field.showWhen;
  if (!cond) return true;
  const data = responses || {};
  if (Array.isArray(cond.allOf)) {
    return cond.allOf.every((c) => isFieldVisible({ showWhen: c }, data));
  }
  if (cond.filled) return isFieldFilled(data[cond.field]);
  if (Object.prototype.hasOwnProperty.call(cond, 'equals')) return data[cond.field] === cond.equals;
  if (Object.prototype.hasOwnProperty.call(cond, 'notEquals')) return data[cond.field] !== cond.notEquals;
  return true;
}

// Fortschritt eines Formulars: { filled, total, pct }. Versteckte (showWhen)
// Felder zaehlen nicht mit, damit die Anzeige ehrlich bleibt.
export function formCompletion(form, responses) {
  const data = responses || {};
  const fields = allFormFields(form).filter((f) => isFieldVisible(f, data));
  const total = fields.length;
  const filled = fields.filter((f) => isFieldFilled(data[f.id])).length;
  const pct = total ? Math.round((filled / total) * 100) : 0;
  return { filled, total, pct };
}

// Menschlich lesbares Label fuer einen choice-/tier-Wert.
export function choiceLabel(field, value) {
  if (!field || !Array.isArray(field.options)) return value;
  const opt = field.options.find((o) => o.value === value);
  return opt ? opt.label : value;
}

// Qualitaets-Urteil fuer eine 1–10 Bewertung: { label, tone }. Jede Stufe hat
// einen eigenen, ehrlichen Text – 1 ist wirklich schlecht, 9 ≠ 10. Fuer andere
// Skalen (max ≠ 10) faellt es auf eine verhaeltnisbasierte Kurzfassung zurueck.
const RATING_VERDICTS_10 = {
  1: { label: 'Unbrauchbar – komplett neu aufsetzen', tone: 'bad' },
  2: { label: 'Sehr schwach', tone: 'bad' },
  3: { label: 'Schwach – großer Verbesserungsbedarf', tone: 'bad' },
  4: { label: 'Unterdurchschnittlich', tone: 'mid' },
  5: { label: 'Mittelmäßig – viel Luft nach oben', tone: 'mid' },
  6: { label: 'Solide Basis', tone: 'mid' },
  7: { label: 'Gut – macht Eindruck', tone: 'good' },
  8: { label: 'Stark', tone: 'good' },
  9: { label: 'Sehr stark – knapp an perfekt', tone: 'good' },
  10: { label: 'Weltklasse – makellos', tone: 'good' },
};
const SESSION_VALUE_VERDICTS_10 = {
  1: { label: 'Unnötig', tone: 'bad' },
  2: { label: 'Geringer Nutzen', tone: 'bad' },
  3: { label: 'Ein paar hilfreiche Punkte', tone: 'bad' },
  4: { label: 'Teils hilfreich', tone: 'mid' },
  5: { label: 'Solider Mittelweg', tone: 'mid' },
  6: { label: 'Guter Value für die Kampagne', tone: 'mid' },
  7: { label: 'Wirkungsvoll – klar weiter gekommen', tone: 'good' },
  8: { label: 'Starker Mehrwert', tone: 'good' },
  9: { label: 'Sehr wertvoll', tone: 'good' },
  10: { label: 'Extrem wertvoll – Kampagne deutlich voran getrieben', tone: 'good' },
};
export function ratingVerdict(value, max = 10, variant = 'quality') {
  const num = Number(value);
  if (!Number.isFinite(num) || num <= 0) return null;
  const map = variant === 'session' ? SESSION_VALUE_VERDICTS_10 : RATING_VERDICTS_10;
  if ((max || 10) === 10 && Number.isInteger(num) && num >= 1 && num <= 10) {
    return map[num];
  }
  const ratio = num / (max || 10);
  if (ratio <= 0.2) return { label: 'Sehr schwach', tone: 'bad' };
  if (ratio <= 0.4) return { label: 'Schwach', tone: 'bad' };
  if (ratio <= 0.6) return { label: 'Mittelmäßig', tone: 'mid' };
  if (ratio <= 0.8) return { label: 'Stark', tone: 'good' };
  return { label: 'Weltklasse', tone: 'good' };
}

// Punktestand einer Checklist: { points, maxPoints, checked, total }.
// Treibt die visuelle "Plus-Punkte"-Belohnung an.
export function checklistScore(field, value) {
  const opts = (field && Array.isArray(field.options) && field.options) || [];
  const selected = Array.isArray(value) ? value : [];
  const maxPoints = opts.reduce((sum, o) => sum + (o.points || 0), 0);
  const points = opts
    .filter((o) => selected.includes(o.value))
    .reduce((sum, o) => sum + (o.points || 0), 0);
  return { points, maxPoints, checked: selected.length, total: opts.length };
}

// Baut die fertige Copy-Paste-Nachricht fuer eine Person (Team-Copywriter oder
// Outreach-Manager). `otherName` ist die jeweils ANDERE Person, mit der der Call
// gemeinsam gefuehrt wird; `protocolUrl`/`protocolKey` sind ihr eigener Link.
// Einzige Quelle der Wahrheit – wird im Protocool-Tab und im Pipeline-Drawer
// identisch genutzt.
export function buildProtocoolShareMessage({
  business,
  researchUrl,
  researchKey,
  researchEmailUrl,
  feedbackDeadline,
  callDate,
  otherName,
  protocolUrl,
  protocolKey,
  nextSteps,
} = {}) {
  const hasDate = !!(callDate && String(callDate).trim());
  const callHeader = hasDate
    ? `STEP 2: Detailing-Call am ${callDate} mit ${otherName || '{Name}'}`
    : `STEP 2: Detailing-Call mit ${otherName || '{Name}'}`;
  const callSentence = hasDate
    ? `Der erste Detailing-Call findet am ${callDate} statt - bitte direkt in den Kalender eintragen.`
    : `Der erste Detailing-Call wird noch terminiert - bitte den Termin direkt in den Kalender eintragen.`;

  // Optionaler NEXT-STEPS-Block (nur in der Outreach-Manager-Nachricht): bittet
  // den Outreach-Manager, die Teams-Einladung an den Team-Copywriter zu senden.
  let nextStepsBlock = '';
  if (nextSteps) {
    const cwName = nextSteps.copywriterName || '{Name des Team-Copywriters}';
    const cwMail = nextSteps.copywriterEmail || '{Mail-Adresse des Team-Copywriters}';
    const ccMail = nextSteps.ccEmail || 'juliprop@hotmail.com';
    nextStepsBlock = `

__________

NEXT STEPS:

- 1. Bitte eine Teams-Einladung für den Detailing-Call an ${cwName} senden. Das ist die Mail-Adresse: ${cwMail} und an ${ccMail} bis morgen VM.
- 2. Bitte um kurze Rückmeldung sobald erledigt 🚀`;
  }

  return `Hier sind alle Infos für ${business || '{Business Name}'} 🏁

STEP 1: Kampagnen-Ideen & Texte

Research-Center:
Hier findest du alles über den Kunden, die Zielgruppen und erste Kampagnen-Ideen.

🔗 ${researchUrl || '{Research-Link}'}
🔑 ${researchKey || '{KEY}'}

Wichtig: Hier kannst du deine ersten 4 E-Mail-Ideen bis zum ${feedbackDeadline || '{Frist}'} ablegen und dem Kunden die Möglichkeit geben, Feedback zu hinterlassen:

🔗 ${researchEmailUrl || '{Research-Email-Link}'}
🔑 ${researchKey || '{KEY}'}

——————————

${callHeader}

${callSentence}

Hier ist der Link zum Protokoll & ein Guide für den Call gemeinsam mit ${otherName || '{Name}'}:

🔗 ${protocolUrl || '{Protokoll-Link}'}
🔑 ${protocolKey || '{KEY}'}

Bitte alle Infos direkt während oder nach dem Call ausfüllen.${nextStepsBlock}`;
}
